/**
 * SearchTeamCell — the My Searches table's Team column.
 *
 * A multi-person icon and the member count in parentheses (`[icon] (4)`), with
 * the full roster — every member and the role they hold — behind a hover.
 *
 * WHY A COUNT AND NOT A FACE PILE. The row must not grow taller (Jared,
 * 2026-08-27). That is the constraint the whole design falls out of: a face
 * pile, a stack of names or a wrapped list of roles all buy their information
 * with vertical space this table does not have, and the table's value is that
 * a recruiter can see many searches at once. So the cell is a summary — one
 * line, the same height as the Imports button beside it — and the detail is an
 * overlay that does not participate in the row's layout at all.
 *
 * WHY THE PANEL IS PORTALLED AND `fixed`, NOT ABSOLUTE. Two reasons, both
 * load-bearing:
 *
 * 1. The table's wrapper is `overflow-x-auto`. CSS computes `overflow-y` to
 *    `auto` when the other axis is not `visible`, so the scroller clips in BOTH
 *    directions — an absolutely-positioned panel (the `Tooltip` primitive's
 *    shape) would be cut off by the container it needs to escape.
 * 2. It has to SCROLL. Dev today has an Open search with 54 members; a flat
 *    list of those is taller than the viewport. `Tooltip` is
 *    `pointer-events-none` by design, so a scrollable panel is not a variant of
 *    it — it is a different component.
 *
 * The `top`/`left` inline style is a MEASURED OFFSET, which is the design
 * system's sanctioned exception to the inline-`style` ban (that ban is on
 * STATIC values — see `.agent/SOP/frontend-design-system/02-tokens.md`). Every
 * colour, size, radius and spacing below is still a token utility.
 *
 * The panel closes on scroll rather than following the trigger: the offset is
 * measured once at open, so a scrolled page would leave it stranded, and a
 * reader who scrolls has moved on from the row they were pointing at.
 */
import type { SearchTeamMember } from "@nu-assist/contracts";
import { Users } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface SearchTeamCellProps {
  /** The whole roster, already ordered by the API (leads first, then role). */
  members: readonly SearchTeamMember[];
  /** Named in the accessible label so the control says WHICH search's team. */
  searchName: string;
}

/** Panel geometry, in px. Mirrors the `w-72` / `max-h-64` utilities below —
 *  used only to decide whether the panel opens downward or flips upward, never
 *  to size it (the classes do that). */
const PANEL_WIDTH = 288;
const PANEL_MAX_HEIGHT = 256;
/** Gap between the trigger and the panel; matches the `mt-1`-scale spacing the
 *  rest of the design system uses for popovers. */
const PANEL_GAP = 4;
/** Grace period before a hover-out closes the panel, so the pointer can cross
 *  the gap between the trigger and the panel it is travelling to. Without it a
 *  54-member roster is unscrollable: the panel closes the moment the pointer
 *  leaves the icon. */
const CLOSE_DELAY_MS = 120;

interface Anchor {
  top: number;
  left: number;
}

/** Where to put the panel so it is on screen: below the trigger when there is
 *  room, flipped above when there is not, and never past the right edge. */
function anchorFor(rect: DOMRect): Anchor {
  const spaceBelow = window.innerHeight - rect.bottom;
  const top =
    spaceBelow < PANEL_MAX_HEIGHT + PANEL_GAP && rect.top > spaceBelow
      ? Math.max(PANEL_GAP, rect.top - PANEL_MAX_HEIGHT - PANEL_GAP)
      : rect.bottom + PANEL_GAP;
  const left = Math.max(
    PANEL_GAP,
    Math.min(rect.left, window.innerWidth - PANEL_WIDTH - PANEL_GAP)
  );
  return { top, left };
}

/** `name ?? email`, the same degradation the Lead column uses — most search
 *  team members are Thrive-only users with no nu_sync federation row. The last
 *  fallback is the id rather than a blank line: the member is REAL and counted,
 *  so the roster has to account for them. */
function memberLabel(member: SearchTeamMember): string {
  return member.name ?? member.email ?? `Thrive user ${member.thrive_user_id}`;
}

export function SearchTeamCell({ members, searchName }: SearchTeamCellProps) {
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelId = useId();

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const close = useCallback(() => {
    cancelClose();
    setAnchor(null);
  }, [cancelClose]);

  const open = useCallback(() => {
    cancelClose();
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setAnchor(anchorFor(rect));
  }, [cancelClose]);

  const closeSoon = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setAnchor(null), CLOSE_DELAY_MS);
  }, [cancelClose]);

  // The offset is measured once, so anything that moves the trigger under it
  // invalidates the panel. Closing is the honest answer — re-measuring on every
  // scroll frame would tie a table of 25 of these to the scroll handler.
  // `capture` is required: the table body scrolls, and a scroll event does not
  // bubble.
  useEffect(() => {
    if (anchor === null) return;
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [anchor, close]);

  useEffect(() => cancelClose, [cancelClose]);

  // No team is a fact about the search, not a number. `(0)` reads as a measured
  // zero-person team; the em dash is this table's existing "nothing here" and
  // matches the Lead and Client cells beside it.
  if (members.length === 0) {
    return (
      <span data-testid="searches-row-team-empty" className="text-text-muted">
        —
      </span>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid="searches-row-team"
        aria-expanded={anchor !== null}
        aria-controls={anchor !== null ? panelId : undefined}
        aria-label={`Show the ${members.length}-person team for ${searchName}`}
        // OPEN, not toggle. A toggle is the obvious shape and it is wrong here:
        // by the time a mouse user's click lands, `onMouseEnter` has ALREADY
        // opened the panel, so the toggle would read the open state and close
        // it — and `mouseenter` does not fire again while the pointer has not
        // left, so the roster would stay shut under a pointer that is still on
        // the trigger. Opening is idempotent, which makes the three ways in
        // (hover, focus, tap) agree. The ways out are Escape, blur and moving
        // the pointer away.
        //
        // The row navigates on click and on Enter/Space, so both are stopped —
        // the same guard the Imports button in this table carries, for the same
        // reason: without it one Enter would open the roster AND leave the page.
        onClick={(e) => {
          e.stopPropagation();
          open();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") e.stopPropagation();
          if (e.key === "Escape") close();
        }}
        onMouseEnter={open}
        onMouseLeave={closeSoon}
        onFocus={open}
        onBlur={closeSoon}
        className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-body text-text-secondary hover:bg-accent-muted hover:text-accent focus:outline-none focus:ring-1 focus:ring-accent"
      >
        <Users className="h-4 w-4" aria-hidden="true" />
        <span className="tabular-nums">({members.length})</span>
      </button>
      {anchor !== null &&
        createPortal(
          <div
            id={panelId}
            data-testid="searches-row-team-panel"
            // Not `role="dialog"`: nothing here traps focus or takes over the
            // page. It is a disclosure, and the button's `aria-expanded` +
            // `aria-controls` already say so.
            onMouseEnter={cancelClose}
            onMouseLeave={closeSoon}
            style={{ top: anchor.top, left: anchor.left }}
            className="fixed z-50 max-h-64 w-72 overflow-y-auto rounded-md border border-border bg-bg p-2 shadow-md"
          >
            <p className="px-1 pb-1 text-label font-semibold text-text-secondary">
              Search team ({members.length})
            </p>
            <ul className="flex flex-col">
              {members.map((member) => (
                <li
                  // Two rows CAN share a `thrive_user_id`: Thrive returns the
                  // same person twice when they hold two roles on one search,
                  // and the roster keeps both because two roles is two facts.
                  key={`${member.thrive_user_id}-${member.role}`}
                  data-testid="searches-row-team-member"
                  className="flex items-baseline justify-between gap-2 rounded-xs px-1 py-0.5 hover:bg-surface-raised"
                >
                  <span className="truncate text-caption text-text" title={memberLabel(member)}>
                    {memberLabel(member)}
                  </span>
                  <span
                    className={`shrink-0 text-label ${
                      member.is_lead ? "font-semibold text-accent" : "text-text-secondary"
                    }`}
                  >
                    {member.role}
                  </span>
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
    </>
  );
}
